import type { EventBus } from './eventBus';

export interface InputState {
  left: boolean;
  right: boolean;
  up: boolean;
  down: boolean;
  jump: boolean;
  fire: boolean;
  charging: boolean;
}

export interface CameraPanState {
  left: boolean;
  right: boolean;
  up: boolean;
  down: boolean;
}

export type InputAction =
  | 'moveLeft'
  | 'moveRight'
  | 'moveUp'
  | 'moveDown'
  | 'cameraPanLeft'
  | 'cameraPanRight'
  | 'cameraPanUp'
  | 'cameraPanDown'
  | 'jump'
  | 'backflip'
  | 'fireStart'
  | 'fireEnd'
  | 'weaponSelect'
  | 'weaponToggle'
  | 'fuseChange'
  | 'ropeExtend'
  | 'ropeRetract'
  | 'ropeRelease'
  | 'pause'
  | 'cameraReset'
  | 'statsToggle'
  | 'girderRotate';

export interface InputCallbacks {
  onAction: (action: InputAction, data?: unknown) => void;
  canProcessInput: () => boolean;
  canControlWorm: () => boolean;
}

export interface KeyBinding {
  code: string;
  action: InputAction;
  data?: unknown;
  onKeyDown?: boolean;
  onKeyUp?: boolean;
  requireControl?: boolean;
  allowRepeat?: boolean;
}

const DEFAULT_KEY_BINDINGS: KeyBinding[] = [
  { code: 'KeyA', action: 'moveLeft', onKeyDown: true, onKeyUp: true },
  { code: 'KeyD', action: 'moveRight', onKeyDown: true, onKeyUp: true },
  { code: 'KeyW', action: 'moveUp', onKeyDown: true, onKeyUp: true },
  { code: 'KeyS', action: 'moveDown', onKeyDown: true, onKeyUp: true },
  { code: 'ArrowLeft', action: 'cameraPanLeft', onKeyDown: true, onKeyUp: true },
  { code: 'ArrowRight', action: 'cameraPanRight', onKeyDown: true, onKeyUp: true },
  { code: 'ArrowUp', action: 'cameraPanUp', onKeyDown: true, onKeyUp: true },
  { code: 'ArrowDown', action: 'cameraPanDown', onKeyDown: true, onKeyUp: true },
  { code: 'Enter', action: 'jump', onKeyDown: true, requireControl: true, allowRepeat: false },
  { code: 'Space', action: 'fireStart', onKeyDown: true },
  { code: 'Space', action: 'fireEnd', onKeyUp: true },
  { code: 'Digit1', action: 'weaponSelect', data: 'grenade', onKeyDown: true },
  { code: 'Digit2', action: 'weaponSelect', data: 'bazooka', onKeyDown: true },
  { code: 'Digit3', action: 'weaponSelect', data: 'dynamite', onKeyDown: true },
  { code: 'Digit4', action: 'weaponSelect', data: 'cluster', onKeyDown: true },
  { code: 'Digit5', action: 'weaponSelect', data: 'shotgun', onKeyDown: true },
  { code: 'Digit6', action: 'weaponSelect', data: 'rope', onKeyDown: true },
  { code: 'Digit7', action: 'weaponSelect', data: 'airstrike', onKeyDown: true },
  { code: 'Digit8', action: 'weaponSelect', data: 'mine', onKeyDown: true },
  { code: 'Digit9', action: 'weaponSelect', data: 'teleport', onKeyDown: true },
  { code: 'Digit0', action: 'weaponSelect', data: 'parachute', onKeyDown: true },
  { code: 'KeyH', action: 'weaponSelect', data: 'homing', onKeyDown: true },
  { code: 'KeyF', action: 'fuseChange', onKeyDown: true },
  { code: 'KeyQ', action: 'ropeRetract', onKeyDown: true, requireControl: true },
  { code: 'KeyE', action: 'ropeExtend', onKeyDown: true, requireControl: true },
  { code: 'KeyR', action: 'ropeRelease', onKeyUp: true, requireControl: true },
  { code: 'Escape', action: 'pause', onKeyDown: true },
  { code: 'Home', action: 'cameraReset', onKeyDown: true },
  { code: 'Tab', action: 'weaponToggle', onKeyDown: true },
  { code: 'Backspace', action: 'weaponToggle', onKeyDown: true },
];

export class InputManager {
  private inputState: InputState = {
    left: false,
    right: false,
    up: false,
    down: false,
    jump: false,
    fire: false,
    charging: false,
  };

  private cameraPanState: CameraPanState = {
    left: false,
    right: false,
    up: false,
    down: false,
  };

  private _eventBus: EventBus | null = null;
  private callbacks: InputCallbacks | null = null;
  private keyBindings: KeyBinding[] = [...DEFAULT_KEY_BINDINGS];
  private enabled = true;

  private lastJumpTime = 0;
  private pendingJump = false;
  private readonly DOUBLE_TAP_THRESHOLD = 250;

  constructor(eventBus?: EventBus) {
    this._eventBus = eventBus ?? null;
  }

  setEventBus(eventBus: EventBus): void {
    this._eventBus = eventBus;
  }

  getEventBus(): EventBus | null {
    return this._eventBus;
  }

  setCallbacks(callbacks: InputCallbacks): void {
    this.callbacks = callbacks;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  getInputState(): Readonly<InputState> {
    return this.inputState;
  }

  getCameraPanState(): Readonly<CameraPanState> {
    return this.cameraPanState;
  }

  setMovement(direction: 'left' | 'right' | 'up' | 'down', pressed: boolean): void {
    this.inputState[direction] = pressed;
  }

  setCharging(charging: boolean): void {
    this.inputState.charging = charging;
  }

  isCharging(): boolean {
    return this.inputState.charging;
  }

  isMoving(): boolean {
    return this.inputState.left || this.inputState.right;
  }

  getMovementDirection(): number {
    if (this.inputState.left && !this.inputState.right) return -1;
    if (this.inputState.right && !this.inputState.left) return 1;
    return 0;
  }

  getAimDirection(): number {
    if (this.inputState.up && !this.inputState.down) return -1;
    if (this.inputState.down && !this.inputState.up) return 1;
    return 0;
  }

  handleKeyDown(e: KeyboardEvent): boolean {
    if (!this.enabled) return false;
    if (!this.callbacks?.canProcessInput()) return false;

    this.updateRawKeyDown(e);

    const binding = this.keyBindings.find(
      (b) => b.code === e.code && b.onKeyDown && (b.allowRepeat !== false || !e.repeat),
    );

    if (!binding) {
      return true;
    }

    if (binding.requireControl && !this.callbacks?.canControlWorm()) {
      return false;
    }

    return this.processAction(binding.action, binding.data, e);
  }

  handleKeyUp(e: KeyboardEvent): boolean {
    if (!this.enabled) return false;

    this.updateRawKeyUp(e);

    const binding = this.keyBindings.find((b) => b.code === e.code && b.onKeyUp);

    if (!binding) {
      return true;
    }

    if (binding.requireControl && !this.callbacks?.canControlWorm()) {
      return false;
    }

    return this.processAction(binding.action, binding.data, e);
  }

  private updateRawKeyDown(e: KeyboardEvent): void {
    switch (e.code) {
      case 'ArrowLeft':
        this.cameraPanState.left = true;
        break;
      case 'ArrowRight':
        this.cameraPanState.right = true;
        break;
      case 'ArrowUp':
        this.cameraPanState.up = true;
        break;
      case 'ArrowDown':
        this.cameraPanState.down = true;
        break;
      case 'KeyA':
        this.inputState.left = true;
        break;
      case 'KeyD':
        this.inputState.right = true;
        break;
      case 'KeyW':
        this.inputState.up = true;
        break;
      case 'KeyS':
        this.inputState.down = true;
        break;
    }
  }

  private updateRawKeyUp(e: KeyboardEvent): void {
    switch (e.code) {
      case 'ArrowLeft':
        this.cameraPanState.left = false;
        break;
      case 'ArrowRight':
        this.cameraPanState.right = false;
        break;
      case 'ArrowUp':
        this.cameraPanState.up = false;
        break;
      case 'ArrowDown':
        this.cameraPanState.down = false;
        break;
      case 'KeyA':
        this.inputState.left = false;
        break;
      case 'KeyD':
        this.inputState.right = false;
        break;
      case 'KeyW':
        this.inputState.up = false;
        break;
      case 'KeyS':
        this.inputState.down = false;
        break;
    }
  }

  private processAction(action: InputAction, data: unknown, e: KeyboardEvent): boolean {
    if (action === 'jump' && !e.repeat) {
      const now = Date.now();
      if (this.pendingJump && now - this.lastJumpTime < this.DOUBLE_TAP_THRESHOLD) {
        this.pendingJump = false;
        this.lastJumpTime = 0;
        this.callbacks?.onAction('backflip');
        return true;
      } else {
        this.lastJumpTime = now;
        this.pendingJump = true;
        this.callbacks?.onAction('jump');
        return true;
      }
    }

    if (action === 'weaponToggle' || action === 'statsToggle') {
      e.preventDefault();
    }

    this.callbacks?.onAction(action, data);
    return true;
  }

  handleGamepadInput(state: { left: boolean; right: boolean; up: boolean; down: boolean }): void {
    if (!this.enabled) return;
    if (!this.callbacks?.canProcessInput()) return;

    const movementChanged =
      this.inputState.left !== state.left || this.inputState.right !== state.right;

    this.inputState.left = state.left;
    this.inputState.right = state.right;
    this.inputState.up = state.up;
    this.inputState.down = state.down;

    if (movementChanged && (state.left || state.right)) {
      this.callbacks?.onAction('moveLeft', { pressed: state.left });
      this.callbacks?.onAction('moveRight', { pressed: state.right });
    }
  }

  reset(): void {
    this.inputState = {
      left: false,
      right: false,
      up: false,
      down: false,
      jump: false,
      fire: false,
      charging: false,
    };
    this.cameraPanState = {
      left: false,
      right: false,
      up: false,
      down: false,
    };
    this.pendingJump = false;
    this.lastJumpTime = 0;
  }

  resetCharging(): void {
    this.inputState.charging = false;
    this.pendingJump = false;
    this.lastJumpTime = 0;
  }

  addKeyBinding(binding: KeyBinding): void {
    this.keyBindings.push(binding);
  }

  removeKeyBinding(code: string, action: InputAction): void {
    this.keyBindings = this.keyBindings.filter((b) => !(b.code === code && b.action === action));
  }

  getKeyBindings(): readonly KeyBinding[] {
    return this.keyBindings;
  }
}
